import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { IAccountingBasisExtendedDTO } from '@modeso/types__aumico-lib-settings-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { Subject, Subscription, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { accountingBasisActions } from '../../actions/settings.actions';
import { AccountingBasisFormComponent } from '../../components/accounting-basis-form/accounting-basis-form.component';
import { SettingsProvider } from '../../providers/settings.provider';
import { getDebug, getTag } from '../../utils/debugUtil';

const filename = 'projects/aumico-lib-settings-fe/src/lib/pages/financial-line-item/financial-line-item.page.ts';
const debug = getDebug(filename);
const TAG = getTag(filename);
@UntilDestroy()
@Component({
    // tslint:disable-next-line: component-selector
    selector: 'settings-accounting-basis',
    templateUrl: './accounting-basis.page.html',
    styleUrls: ['./accounting-basis.page.scss'],
})
export class AccountingBasisPage implements OnInit, OnDestroy {
    public data: IAccountingBasisExtendedDTO[];
    private currentAccountingBasis: IAccountingBasisExtendedDTO | undefined;

    private errorMessage = new Subject<HttpErrorResponse>();

    private destroy$ = new Subject<void>();

    @Input() public set injectedProviderSetter(injectedProvider: SettingsProvider) {
        if (injectedProvider) {
            this.provider = injectedProvider;
        }
    }

    @Input() languages: string[] = ['de-ch', 'en-us'];
    @Input() countries: INameId[] = [];

    constructor(
        private provider: SettingsProvider,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<AccountingBasisFormComponent>,
        protected actionsListener$: ActionsSubject,
    ) {}

    private subscriptions: Subscription[];
    ngOnInit() {
        this.subscriptions = [];
        this.subscriptions.push(this.provider.allAccountingBasis$().subscribe({ next: (data) => (this.data = data) }));
        this.provider.dispatchGetAllAccountingBasis();
        this.subscriptions.push(
            this.provider.getActiveAccountingBasis$().subscribe({
                next: (item: IAccountingBasisExtendedDTO) => {
                    this.currentAccountingBasis = item;
                },
            }),
        );

        this.provider.accountingBasisError$().pipe(untilDestroyed(this)).subscribe(this.errorMessage);

        this.subscriptions.push(
            this.actionsListener$
                .pipe(ofType(accountingBasisActions.onSubmitCreateAccountingBasisSuccess), untilDestroyed(this))
                .subscribe(() => this.onSaveSuccessfully()),
        );
        this.subscriptions.push(
            this.actionsListener$
                .pipe(ofType(accountingBasisActions.onSubmitEditAccountingBasisSuccess), untilDestroyed(this))
                .subscribe(() => this.onSaveSuccessfully()),
        );
    }

    public editPressedEventHandler(item: string): void {
        this.provider.dispatchSetActiveAccountingBasis(item);
        this.openDialog();
    }

    onCreateAccountingBasisPressed() {
        this.provider.dispatchSetActiveAccountingBasis(null);
        this.openDialog();
    }

    openDialog(): void {
        this.dialogRef = this.dialog.open(AccountingBasisFormComponent, {
            width: '70%',
            data: {
                accountingBasis: this.currentAccountingBasis,
                errorMessage: this.errorMessage,
                countries: this.countries,
            },
        });
        this.dialogRef.componentInstance.languages = this.languages;
        this.dialogRef.componentInstance.savePressed.pipe(takeUntil(this.destroy$)).subscribe((result) => {
            if (result._id == null) {
                this.provider.dispatchCreateAccountingBasis(result);
            } else {
                this.provider.dispatchUpdateAccountingBasis({
                    id: result._id,
                    payload: { text: result.text, countryISO31661Alpha2Codes: result.countryISO31661Alpha2Codes },
                });
            }
        });
        this.dialogRef.afterClosed().subscribe(() => {
            // destroying subscriptions that were created upon opening the dialog
            this.destroyDialogSubscriptions();
        });
    }

    private onSaveSuccessfully() {
        this.dialogRef.close();
        Swal.fire({
            icon: 'info',
            title: 'done editing/creating',
            showCloseButton: true,
            timer: 2000,
        });
        this.provider.dispatchGetAllAccountingBasis();
    }

    destroyDialogSubscriptions(): void {
        this.destroy$.next();
    }

    ngOnDestroy(): void {
        this.destroyDialogSubscriptions();
        this.destroy$.complete();
        this.subscriptions.forEach((subscribe) => {
            subscribe.unsubscribe();
        });
    }
}
